import type { CaptureResult } from 'posthog-js'

const REDACTED = '<redacted>'

const SENSITIVE_PARAM_NAMES = new Set([
  'access_token',
  'refresh_token',
  'id_token',
  'provider_token',
  'provider_refresh_token',
  'token',
  'code',
])

// The phone capture route (`/<locale>/capture`) carries the live capture
// session token in the URL hash (`#s=<session>&t=<token>&h=<holdout>`) under
// deliberately short keys that are NOT in SENSITIVE_PARAM_NAMES. Adding `t`/`s`
// to that set would over-redact unrelated URLs, so instead we drop the whole
// fragment whenever the pathname is the capture route.
const CAPTURE_ROUTE_PATTERN = /\/capture\/?$/

// A support-sent password reset link carries its token as a path segment
// (`/auth/reset/<token>`, redeemed at `/public/password-resets/<token>`); the
// token signs its holder in, so the segment is redacted wherever a URL is
// recorded. The reset page also stops replay on mount (analytics.ts).
const TOKEN_PATH_PATTERNS = [/(\/auth\/reset\/)[^/?#]+/, /(\/password-resets\/)[^/?#]+/]

function redactTokenPath(pathname: string): string {
  return TOKEN_PATH_PATTERNS.reduce((out, pattern) => out.replace(pattern, `$1${REDACTED}`), pathname)
}

/**
 * Whether a page URL carries a credential in its path. Session replay (PostHog
 * and Sentry) snapshots the page URL outside any event hook, so replay is not
 * started on such a page (analytics.ts, instrumentation-client.ts).
 */
export function isTokenBearingPath(pathname: string): boolean {
  return TOKEN_PATH_PATTERNS.some(pattern => pattern.test(pathname))
}

const URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$pathname',
  '$prev_pageview_pathname',
  '$initial_current_url',
  '$initial_pathname',
  '$referrer',
  // PostHog stamps the session's first URL onto every event in the session via
  // these autocaptured properties. On the capture route that first URL carries
  // the token-bearing hash, so they leak it exactly like `$current_url` unless
  // they go through the same sanitizer.
  '$session_entry_url',
  '$session_entry_referrer',
  'current_url',
  'url',
  'href',
  'referrer',
  'redirect_url',
  'redirect_to',
  'return_url',
])

const sensitiveParamPattern = Array.from(SENSITIVE_PARAM_NAMES).join('|')
const sensitiveParamRegex = new RegExp(`([?&#](${sensitiveParamPattern})=)[^&#]+`, 'gi')

function hashContainsSensitiveParams(hash: string): boolean {
  if (!hash) return false
  const hashParams = hash.startsWith('#') ? hash.slice(1) : hash
  if (!hashParams) return false
  return Array.from(new URLSearchParams(hashParams).keys()).some(key => SENSITIVE_PARAM_NAMES.has(key.toLowerCase()))
}

export function sanitizeTelemetryUrl(value: string): string {
  if (!value) return value

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://martini.local'
    const url = new URL(value, base)
    url.pathname = redactTokenPath(url.pathname)
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_PARAM_NAMES.has(key.toLowerCase())) url.searchParams.set(key, REDACTED)
    }
    if (url.hash && (CAPTURE_ROUTE_PATTERN.test(url.pathname) || hashContainsSensitiveParams(url.hash))) {
      url.hash = '#redacted'
    }
    // Keep genuinely path-relative inputs ('/foo') relative, but NOT
    // protocol-relative ones ('//host/foo') — the relative form would silently
    // drop the host and corrupt the recorded URL. Let those fall through to the
    // absolute serialization, which preserves the host.
    const isPathRelative = value.startsWith('/') && !value.startsWith('//')
    return isPathRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return redactTokenPath(value.replace(sensitiveParamRegex, `$1${REDACTED}`))
  }
}

function sanitizePropertyMap(properties: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!properties) return properties
  let sanitized: Record<string, unknown> | null = null

  for (const [key, propValue] of Object.entries(properties)) {
    if (typeof propValue !== 'string' || !URL_PROPERTY_KEYS.has(key)) continue
    const nextValue = sanitizeTelemetryUrl(propValue)
    if (nextValue === propValue) continue
    if (!sanitized) sanitized = { ...properties }
    sanitized[key] = nextValue
  }

  return sanitized ?? properties
}

export function sanitizePostHogCaptureResult(result: CaptureResult | null): CaptureResult | null {
  if (!result) return result
  const properties = sanitizePropertyMap(result.properties)
  const setProperties = sanitizePropertyMap(result.$set)
  const setOnceProperties = sanitizePropertyMap(result.$set_once)
  if (properties === result.properties && setProperties === result.$set && setOnceProperties === result.$set_once) {
    return result
  }
  return {
    ...result,
    properties: properties ?? result.properties,
    $set: setProperties,
    $set_once: setOnceProperties,
  }
}

/** A fetch/XHR breadcrumb or an http span: the URL lives in `data` under one of these keys. */
const SPAN_URL_KEYS = new Set(['url', 'http.url', 'url.full', 'http.target'])

export interface SentryLikeBreadcrumb {
  message?: string
  data?: Record<string, unknown>
}

export interface SentryLikeSpan {
  description?: string
  data?: Record<string, unknown>
}

export interface SentryLikeEvent {
  /** A server transaction is named after its route; a raw path lands here when the route is not parameterized. */
  transaction?: string
  request?: {
    url?: string
    headers?: Record<string, string | undefined>
  }
  extra?: Record<string, unknown>
  tags?: Record<string, unknown>
  /** `contexts.trace.data` carries the root span's attributes (`url.full`, `http.target`) on a server transaction. */
  contexts?: Record<string, unknown>
  breadcrumbs?: SentryLikeBreadcrumb[]
  spans?: SentryLikeSpan[]
}

function sanitizeDataUrls<T extends Record<string, unknown> | undefined>(data: T): T {
  if (!data) return data
  let sanitized: Record<string, unknown> | null = null
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string' || !SPAN_URL_KEYS.has(key)) continue
    const next = sanitizeTelemetryUrl(value)
    if (next === value) continue
    if (!sanitized) sanitized = { ...data }
    sanitized[key] = next
  }
  return (sanitized ?? data) as T
}

/**
 * Sentry records every fetch as a breadcrumb (`data.url`) and, with tracing
 * on, as a span whose description is `GET <url>`; neither passes through
 * `beforeSend`'s request/extra fields, so they are scrubbed on their own.
 */
export function sanitizeSentryBreadcrumb<T extends SentryLikeBreadcrumb>(breadcrumb: T): T {
  const data = sanitizeDataUrls(breadcrumb.data)
  return data === breadcrumb.data ? breadcrumb : { ...breadcrumb, data }
}

function sanitizeSentrySpan<T extends SentryLikeSpan>(span: T): T {
  const data = sanitizeDataUrls(span.data)
  const description = typeof span.description === 'string' ? redactTokenPath(span.description) : span.description
  return data === span.data && description === span.description ? span : { ...span, data, description }
}

function sanitizeAll<T>(items: T[] | undefined, sanitize: (item: T) => T): T[] | undefined {
  if (!items) return items
  let changed = false
  const next = items.map(item => {
    const out = sanitize(item)
    if (out !== item) changed = true
    return out
  })
  return changed ? next : items
}

export function sanitizeSentryEvent<T extends SentryLikeEvent>(event: T): T {
  const requestUrl = event.request?.url
  const sanitizedRequestUrl = typeof requestUrl === 'string' ? sanitizeTelemetryUrl(requestUrl) : requestUrl
  const transaction = typeof event.transaction === 'string' ? redactTokenPath(event.transaction) : event.transaction
  const extra = sanitizePropertyMap(event.extra)
  const contexts = sanitizeTraceContext(event.contexts)
  const breadcrumbs = sanitizeAll(event.breadcrumbs, sanitizeSentryBreadcrumb)
  const spans = sanitizeAll(event.spans, sanitizeSentrySpan)

  if (
    sanitizedRequestUrl === requestUrl &&
    transaction === event.transaction &&
    extra === event.extra &&
    contexts === event.contexts &&
    breadcrumbs === event.breadcrumbs &&
    spans === event.spans
  ) {
    return event
  }

  return {
    ...event,
    transaction,
    request: event.request
      ? {
          ...event.request,
          url: sanitizedRequestUrl,
        }
      : event.request,
    extra,
    contexts,
    breadcrumbs,
    spans,
  }
}

/**
 * On a server or edge transaction the incoming request's URL is an attribute
 * of the root span, which Sentry serializes as `contexts.trace.data`, not as a
 * span in `spans`. The same keys as a span's data are scrubbed.
 */
function sanitizeTraceContext<T extends Record<string, unknown> | undefined>(contexts: T): T {
  const trace = contexts?.trace
  if (!trace || typeof trace !== 'object') return contexts
  const traceData = (trace as { data?: Record<string, unknown> }).data
  const data = sanitizeDataUrls(traceData)
  if (data === traceData) return contexts
  return { ...contexts, trace: { ...trace, data } } as T
}

/** Vercel Web Analytics sends the raw page path as the pageview; it goes through the same redaction. */
export function sanitizeVercelAnalyticsEvent<T extends { url: string }>(event: T): T {
  const url = sanitizeTelemetryUrl(event.url)
  return url === event.url ? event : { ...event, url }
}
